import { faCheck, faRefresh, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Button,
	Form,
	FormControl,
	FormGroup,
	FormLabel,
	FormText,
	Select,
	Toggle
} from 'vio-components';
import { convertToSentenceCase } from 'vio-libs';

import { IPreviewFiber } from '../interfaces';
import { isBackboneFiber } from '../lib';

interface IAddUpdateFormProps {
	fiber: any;
	handleCancel: () => void;
	handleSubmit: (formValues: any) => void;
	isAddPreviewMode: boolean;
	isEditMode: boolean;
	isUploadMode: boolean;
	previewFile: IPreviewFiber;
}

const initialFormValues = {
	backbone: false,
	file: undefined,
	future: false,
	name: '',
	type: 'fiber'
};
const formatAssetName = (name: string) => {
	return  convertToSentenceCase(name.replace('.kml', ''));
};

const AddUpdateForm: React.FC<IAddUpdateFormProps> = ({
	fiber,
	handleCancel,
	handleSubmit,
	isAddPreviewMode = false,
	isEditMode = false,
	isUploadMode = false,
	previewFile
}): JSX.Element => {
	const { t } = useTranslation();

	const getInitialFormValues = () => {
		if (fiber) {
			return fiber;
		}

		if (isAddPreviewMode && previewFile) {
			return {
				...initialFormValues,
				file: previewFile.file,
				name: formatAssetName(previewFile.name)
			};
		}

		return initialFormValues;
	};
	const [ formValues, setFormValues ] = useState(getInitialFormValues());
	const [ isFormValid, setIsFormValid ] = useState(false);
	const layerTypeOptions = [
		{ label: 'Fiber', value: 'fiber' }
	];

	const getTypeValue = () => layerTypeOptions.filter((option) => option.value === formValues.type);

	const addFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = e.target;
		const file = files [ 0 ];
		const nameOverride = formValues?.name || formatAssetName(file?.name);

		setFormValues({
			...formValues,
			file,
			name: nameOverride
		});
	};

	const handleTypeChange = (type: { label: string; value: any }) => {
		setFormValues({ ...formValues, type: type.value });
	};

	const handleFutureToggle = () => {
		setFormValues({
			...formValues,
			future: !formValues.future
		});
	};
	const handleBackboneToggle = () => {
		let { name } = formValues;
		const backbone = !formValues.backbone;

		if (backbone) {
			if (!isBackboneFiber(name)) {
				name = `${formValues.name} Backbone`;
			}
		}
		else {
			if (isBackboneFiber(name)) {
				name = formValues.name.replace(/backbone/ig, '').trim();
			}
		}

		setFormValues({ ...formValues, backbone, name });
	};

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormValues({ ...formValues, name: e.target.value });
	};

	const onReset = () => {
		setFormValues(fiber || initialFormValues);
	};

	useEffect(() => {
		if (!formValues) {
			return;
		}

		if (!isEqual(formValues, fiber)) {
			const { name } = formValues;

			if (!isEditMode && name) {
				setIsFormValid(true);
			}

			if (isEditMode) {
				setIsFormValid(true);
			}
		}
	}, [ formValues ]);

	return (
		<Form>
			{ (!isEditMode && !isAddPreviewMode) && <FormGroup id="file">
				{ !isUploadMode && <FormLabel required>Upload Asset</FormLabel> }

				<FormText>Choose a .kml or .geoson file to upload</FormText>

				<FormControl
					className='file-upload'
					type='file'
					accept='.kml, .geojson'
					onChange={ addFile } />
			</FormGroup> }

			{ !isUploadMode && <>
				<FormGroup id="name">
					<FormLabel required={ isEditMode }>Asset Name</FormLabel>

					<FormText>Optional. Override name from file upload</FormText>

					<FormControl
						disabled={ !isEditMode && !formValues.file }
						type="text"
						placeholder="Name"
						value={ formValues?.name }
						onChange={ handleNameChange } />
				</FormGroup>

				<FormGroup id="type">
					<FormLabel>Asset Type</FormLabel>

					<FormText>Currently only Fiber is available</FormText>

					<Select
						onChange={ handleTypeChange }
						value={ getTypeValue() }
						isDisabled
						options={ layerTypeOptions }
						showDropdownIndicator={ false } />
				</FormGroup>

				<FormGroup id="future">
					<FormLabel>Active Asset</FormLabel>

					<FormText>Designate asset as active and available</FormText>

					<Toggle
						disabled={ !formValues.name }
						checked={ !formValues?.future }
						onToggleClick={ handleFutureToggle } />
				</FormGroup>

				{ formValues.type === 'fiber' && <FormGroup id="backbone">
					<FormLabel>Fiber Backbone</FormLabel>

					<FormText>Is the fiber a backbone that connects markets together? <br /> Choosing this will update the fiber name above</FormText>

					<Toggle
						disabled={ !formValues.name }
						checked={ formValues?.backbone }
						onToggleClick={ handleBackboneToggle } />
				</FormGroup> }

			</> }

			<div className='vio-form-footer'>
				{ isEditMode && <Button
					className='reset'
					outline
					onClick={ onReset }>
					{ t('RESET') }<FontAwesomeIcon icon={ faRefresh } aria-hidden transform='down-1 right-2'/>
				</Button> }

				<Button
					outline
					className='cancel'
					onClick={ handleCancel }>
					{ t('CANCEL') } <FontAwesomeIcon icon={ faTimes } aria-hidden transform='down-1 right-2'/>
				</Button>

				<Button
					disabled={ !isFormValid }
					className='submit'
					onClick={ () => handleSubmit(formValues) }
					variant="success">
					{ t('SUBMIT') } <FontAwesomeIcon icon={ faCheck } aria-hidden transform='down-1 right-2' />
				</Button>
			</div>
		</Form>
	);
};

export default AddUpdateForm;

